import React, { useEffect, useState } from "react";
import { Spin, Button, Modal, Form, Input, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { postApi } from "../../redux/rest/restSlice";
import get from "lodash/get";
import { generatePaginationObj, TABLE_PAGINATION } from "../../utilities/helper";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  LIST_USERS,
  CREATE_USERS,
  UPDATE_USERS,
} from "../../constants/actionTypes";
import { usersTable } from "../../constants/users";
import Can from "../Can";
import DataTable from "../DataTable";
import { PERMISSIONS } from "../../constants/permissions";
import { hasAllPermissions } from "../../redux/selectors/authSelector";

const UsersTable = () => {
  const [loading, setLoading] = useState(true);
  const [modalLoading, setModalLoading] = useState(false);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const [emailSearch, setEmailSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [form] = Form.useForm();

  const [usersData, setUsersData] = useState({
    data: [],
    pagination: {
      offset: 0,
      limit: 10,
      total: 0,
      current: 1,
    },
  });

  const canEditUser = useSelector(
    hasAllPermissions([PERMISSIONS.UPDATE_USERS])
  );

  const dispatch = useDispatch();

  /* ---------------- Fetch Users ---------------- */
  // Refreshes (paging, create/edit) keep the current email search
  const fetchUsersList = (
    page = 1,
    limit = get(usersData, "pagination.limit"),
    search = appliedSearch
  ) => {
    setLoading(true);
    const pageObj = generatePaginationObj(page, limit);
    const term = (search || "").trim();

    dispatch(
      postApi({
        endpoint: LIST_USERS,
        payload: {
          filters: term ? { email_address: term } : {},
          ...pageObj,
        },
      })
    )
      .then(unwrapResult)
      .then((result) => {
        setUsersData({
          data: get(result, "data"),
          pagination: {
            ...pageObj,
            total: get(result, "total"),
            current: page,
          },
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchUsersList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearchEmail = (value) => {
    setAppliedSearch(value);
    fetchUsersList(1, undefined, value);
  };

  const clearSearch = () => {
    setEmailSearch("");
    onSearchEmail("");
  };

  /* ---------------- Create User ---------------- */
  const openCreateModal = () => {
    form.resetFields();
    setIsCreateModalOpen(true);
  };

  const handleCreateUser = () => {
    form.validateFields().then((values) => {
      setModalLoading(true);

      dispatch(
        postApi({
          endpoint: CREATE_USERS,
          payload: values,
        })
      )
        .then(unwrapResult)
        .then(() => {
          message.success("User created successfully");
          setIsCreateModalOpen(false);
          fetchUsersList(usersData.pagination.current);
        })
        .finally(() => setModalLoading(false));
    });
  };

  /* ---------------- Edit User ---------------- */
  const openEditModal = (record) => {
    setEditingUser(record);
    form.setFieldsValue({
      first_name: record.first_name,
      last_name: record.last_name,
      email_address: record.email_address,
    });
    setIsEditModalOpen(true);
  };

  const handleEditUser = () => {
    form.validateFields().then((values) => {
      setModalLoading(true);

      dispatch(
        postApi({
          endpoint: UPDATE_USERS,
          payload: {
            id: editingUser.id,
            ...values,
          },
        })
      )
        .then(unwrapResult)
        .then(() => {
          message.success("User updated successfully");
          setIsEditModalOpen(false);
          fetchUsersList(usersData.pagination.current);
        })
        .finally(() => setModalLoading(false));
    });
  };

  const rows = get(usersData, "data");
  const firstLoad = loading && !(rows && rows.length);

  return (
    <Spin spinning={loading && !firstLoad}>
      {/* 🔹 Header */}
      <div className="list-toolbar">
        <div className="list-toolbar__main">
          <Input.Search
            className="list-toolbar__search"
            placeholder="Search by Email Address"
            allowClear
            value={emailSearch}
            onChange={(e) => setEmailSearch(e.target.value)}
            onSearch={onSearchEmail}
          />
        </div>

        <Can permissions={[PERMISSIONS.CREATE_USERS]}>
          <div className="list-toolbar__actions">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateModal}
            >
              Add User
            </Button>
          </div>
        </Can>
      </div>

      {/* 🔹 Table */}
      <DataTable
        skeleton={firstLoad}
        scrollX={800}
        rowKey="id"
        className="mt-20"
        dataSource={rows}
        columns={usersTable(
          get(usersData, "pagination.offset"),
          openEditModal,
          canEditUser
        )}
        pagination={{
          ...TABLE_PAGINATION,
          pageSize: get(usersData, "pagination.limit"),
          total: get(usersData, "pagination.total"),
          current: get(usersData, "pagination.current"),
        }}
        onChange={(paginationObj) => {
          // Page change, or page size change (back to page 1)
          const pageSize = get(paginationObj, "pageSize");
          const sizeChanged = pageSize !== get(usersData, "pagination.limit");

          fetchUsersList(
            sizeChanged ? 1 : get(paginationObj, "current"),
            pageSize
          );
        }}
        emptyProps={{
          noun: "users",
          search: appliedSearch,
          onClearSearch: clearSearch,
        }}
      />

      {/* 🔹 Create User Modal */}
      <Modal
        title="Add User"
        open={isCreateModalOpen}
        onCancel={() => setIsCreateModalOpen(false)}
        onOk={handleCreateUser}
        confirmLoading={modalLoading}
        okText="Done"
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            label="First Name"
            name="first_name"
            rules={[{ required: true, message: "First name is required" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item label="Last Name" name="last_name">
            <Input />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email_address"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Invalid email format" },
            ]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* 🔹 Edit User Modal */}
      <Modal
        title="Edit User"
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        onOk={handleEditUser}
        confirmLoading={modalLoading}
        okText="Done"
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            label="First Name"
            name="first_name"
            rules={[{ required: true, message: "First name is required" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item label="Last Name" name="last_name">
            <Input />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email_address"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Invalid email format" },
            ]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </Spin>
  );
};

export default UsersTable;
